import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, t } from '../../../shared/constants/dashboardConstants';
import { useAuth } from '../../../contexts/AuthContext';
import { upgradeManagedAccount } from '../../../api/userApi';

const UNLOCKED = [
  '공고 직접 등록',
  '보관함 (관심 배우 저장)',
  '일정 관리',
  '배우와 메시지',
  '제작사 정보 편집',
];

/**
 * 대행(MANAGED) 계정 → 정식(NORMAL) 승격.
 * 신규 가입이 아니라 등급 변경이므로 지금 공고·지원자 데이터가 그대로 남는다.
 */
export default function UpgradeAccountSection() {
  const navigate = useNavigate();
  const { isManagedAccount, refreshSession } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  if (!isManagedAccount && !done) return null;

  const handleUpgrade = async () => {
    if (saving) return;
    if (!window.confirm('정식 계정으로 전환할까요?\n지금 계정과 공고·지원자 데이터는 그대로 유지됩니다.')) return;
    setSaving(true);
    setError(null);
    try {
      await upgradeManagedAccount();
      // 서버 상태가 바뀌었으므로 세션을 다시 읽어 사이드바를 펼친다
      await refreshSession();
      setDone(true);
    } catch (err) {
      const status = err.response?.status;
      setError(
        status === 404 || status === 405
          ? '전환 기능이 아직 배포되지 않았습니다. 잠시 후 다시 시도해주세요.'
          : (err.response?.data?.message ?? `전환에 실패했습니다. (HTTP ${status ?? '네트워크'})`)
      );
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div style={{
        border: `1px solid ${C.g200}`, borderRadius: '16px', padding: '24px',
        display: 'flex', flexDirection: 'column', gap: '12px',
      }}>
        <span style={t('18px', 700, C.bk)}>정식 계정으로 전환되었습니다</span>
        <span style={{ ...t('14px', 400, C.sub), lineHeight: 1.6 }}>
          이제 모든 기능을 사용할 수 있습니다. 왼쪽 메뉴에서 확인해보세요.
        </span>
        <button
          onClick={() => navigate('/producer/dashboard')}
          style={{
            alignSelf: 'flex-start', height: '44px', padding: '0 20px', borderRadius: '10px',
            border: 'none', background: C.bk, cursor: 'pointer', ...t('14px', 600, C.white),
          }}
        >
          대시보드로 가기
        </button>
      </div>
    );
  }

  return (
    <div style={{
      border: `1px solid ${C.g200}`, borderRadius: '16px', padding: '24px',
      display: 'flex', flexDirection: 'column', gap: '16px',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <span style={t('18px', 700, C.bk)}>정식 계정으로 전환</span>
        <span style={{ ...t('14px', 400, C.sub), lineHeight: 1.6 }}>
          지금은 캐스타가 개설한 대행 계정으로, 공고·지원자 관리만 사용할 수 있습니다.<br />
          전환하면 아래 기능이 열리며, <b>현재 계정과 데이터는 그대로 유지</b>됩니다.
        </span>
      </div>

      <ul style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: 0, padding: 0 }}>
        {UNLOCKED.map(item => (
          <li key={item} style={{ display: 'flex', alignItems: 'center', gap: '8px', listStyle: 'none' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M20 6L9 17l-5-5" stroke={C.green} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={t('14px', 500, C.bk)}>{item}</span>
          </li>
        ))}
      </ul>

      {error && <span style={t('13px', 500, '#EF4444')}>{error}</span>}

      <button
        onClick={handleUpgrade}
        disabled={saving}
        style={{
          alignSelf: 'flex-start', height: '48px', padding: '0 24px', borderRadius: '12px',
          border: 'none', background: saving ? C.g200 : C.bk,
          cursor: saving ? 'default' : 'pointer', ...t('15px', 600, C.white),
        }}
      >
        {saving ? '전환 중...' : '정식 계정으로 전환'}
      </button>
    </div>
  );
}
